import {
  useMutation,
  useQuery,
  type QueryFunctionContext,
} from "@tanstack/react-query";
import { getCsrfCookie } from "../lib/utils";

export type PhotoVocabularyImportMode =
  | "ALL_WORDS"
  | "HIGHLIGHTED_ONLY";

export type PhotoVocabularyImportStatus =
  | "PENDING"
  | "PROCESSING"
  | "COMPLETE"
  | "FAILED";

export interface PhotoVocabularyImportWord {
  token_id: string;
  word: string;
  sentence: string;
  language: string;
  confidence?: number;
}

export interface PhotoVocabularyImportDto {
  id: number;
  image: string;
  mode: PhotoVocabularyImportMode;
  status: PhotoVocabularyImportStatus;
  created_at: string;
  updated_at: string;
  detected_language: string | null;
  result_payload: PhotoVocabularyImportWord[];
  error_message: string | null;
  imported_vocabulary_count: number;
}

const PHOTO_VOCABULARY_IMPORT_QUERY_KEY =
  "photo-vocabulary-import";

export function usePhotoVocabularyImportCreate() {
  return useMutation({
    mutationFn: async (arg: {
      file: File;
      mode: PhotoVocabularyImportMode;
    }) => {
      const formData = new FormData();
      formData.append("image", arg.file);
      formData.append("mode", arg.mode);
      const res = await fetch("/api/reeprise/photo_vocabulary_imports/", {
        method: "POST",
        headers: {
          "X-CSRFTOKEN": getCsrfCookie(),
        },
        body: formData,
      });
      if (!res.ok) {
        throw new Error("Failed to start photo vocabulary import.");
      }
      return (await res.json()) as PhotoVocabularyImportDto;
    },
  });
}

export function usePhotoVocabularyImport(
  importId?: number | null,
) {
  return useQuery({
    queryKey: [PHOTO_VOCABULARY_IMPORT_QUERY_KEY, importId],
    queryFn: async ({ signal }: QueryFunctionContext) => {
      const res = await fetch(
        `/api/reeprise/photo_vocabulary_imports/${importId}/`,
        { signal },
      );
      if (!res.ok) {
        throw new Error("Failed to load photo import status.");
      }
      return (await res.json()) as PhotoVocabularyImportDto;
    },
    enabled: Boolean(importId),
    refetchInterval: (query) => {
      const data = query.state.data as
        | PhotoVocabularyImportDto
        | undefined;
      if (!data) {
        return false;
      }
      if (
        data.status === "COMPLETE" ||
        data.status === "FAILED"
      ) {
        return false;
      }
      return 1500;
    },
  });
}

export function usePhotoVocabularyImportFinalize() {
  return useMutation({
    mutationFn: async (arg: {
      importId: number;
      tokenIds: string[];
      targetLanguages?: string[];
      learningLanguage?: string | null;
    }) => {
      const res = await fetch(
        `/api/reeprise/photo_vocabulary_imports/${arg.importId}/finalize/`,
        {
          method: "POST",
          headers: {
            "X-CSRFTOKEN": getCsrfCookie(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            token_ids: arg.tokenIds,
            // only send if provided to avoid DRF complaining on empty lists/blank strings
            ...(arg.targetLanguages && arg.targetLanguages.length > 0
              ? { target_languages: arg.targetLanguages }
              : {}),
            ...(arg.learningLanguage
              ? { learning_language: arg.learningLanguage }
              : {}),
          }),
        },
      );
      if (!res.ok) {
        throw new Error("Failed to import selected words.");
      }
      return (await res.json()) as {
        created_vocabulary_ids: number[];
        translations: {
          token_id: string;
          vocabulary_id: number;
          translation_language: string;
          translation_word: string;
          translation_sentence: string;
        }[];
      };
    },
  });
}

export function usePhotoVocabularyImportPreviewTranslations() {
  return useMutation({
    mutationFn: async (arg: {
      importId: number;
      tokenIds: string[];
      targetLanguages?: string[];
      learningLanguage?: string | null;
    }) => {
      const res = await fetch(
        `/api/reeprise/photo_vocabulary_imports/${arg.importId}/preview_translations/`,
        {
          method: "POST",
          headers: {
            "X-CSRFTOKEN": getCsrfCookie(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            token_ids: arg.tokenIds,
            target_languages: arg.targetLanguages,
            learning_language: arg.learningLanguage,
          }),
        },
      );
      if (!res.ok) {
        throw new Error("Failed to preview translations.");
      }
      return (await res.json()) as {
        translations: {
          token_id: string;
          translation_language: string;
          translation_word: string;
          translation_sentence: string;
        }[];
      };
    },
  });
}
